import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./business-card-scanner-section.scss";
import ScannerIcon from "../../../../assets/icons/scanner.svg";
import { ReactComponent as StepIcon } from "../../../../assets/icons/step-icon.svg";
import { ReactComponent as ArrowIcon } from "../../../../assets/icons/step-arrow.svg";
import BusinessCardScannerDialog from "./business-card-scanner-dialog";
import ScanCompleteDialog from "./scan-complete-dialog";
import { AlertType, BusinessCardType } from "../../../../utils/util-types";
import { routes } from "../../../../constants/routes";
import { leadCreate } from "../../../../api/lead";
import { ALERT_TYPE } from "../../../../utils/constants";
import store from "../../../../store";

interface BusinessCardScannerProps {
  setLoading: (isLoading: boolean) => void;
  setAlertOpen: (isOpen: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertType: (type: AlertType) => void;
}
const SCAN_COMPLETE_ACTION = {
  VIEW_DRAFT: 1,
  VERIFY_LATER: 2,
};

const BusinessCardScanner: React.FC<BusinessCardScannerProps> = ({
  setLoading,
  setAlertOpen,
  setAlertMessage,
  setAlertType,
}) => {
  const [showScannerDialog, setShowScannerDialog] = useState<boolean>(false);
  const [showScanCompleteDialog, setShowScanCompleteDialog] =
    useState<boolean>(false);
  const [cardScanValue, setCardScanValue] = useState<BusinessCardType | null>(
    null,
  );
  const [editMode, setEditMode] = useState(store.getState().dashboard.editMode);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setEditMode(store.getState().dashboard.editMode);
    });

    return () => unsubscribe();
  }, []);

  function getNameParts(fullName: string) {
    const parts = fullName.trim().split(" ");

    return {
      firstName: parts[0] || null,
      middleName: parts.length > 2 ? parts[1] : null,
      lastName: parts.length > 1 ? parts[parts.length - 1] : null,
    };
  }

  const createDraftLead = async (action: number) => {
    setShowScanCompleteDialog(false);
    try {
      const nameParts = getNameParts(cardScanValue?.name || "");
      const payload: any = {
        company: cardScanValue?.organization,
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        primaryEmail: cardScanValue?.email,
        primaryPhoneNumber: cardScanValue?.phone,
        website: cardScanValue?.website || null,
        leadSource: "Manual",
        leadStatus: "Draft",
        designation: cardScanValue?.designation,
      };
      if (nameParts?.middleName) {
        payload.middleName = nameParts?.middleName;
      }

      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]: any) => {
        if (value) formData.append(key, value);
      });

      if (cardScanValue?.address) {
        formData.append(`address[streetLine1]`, cardScanValue?.address);
      }
      setLoading(true);
      const response = await leadCreate(formData);
      if (response.data.success) {
        setAlertMessage("Lead saved successfully!");
        setAlertType(ALERT_TYPE.SUCCESS);
        setAlertOpen(true);
        if (action === SCAN_COMPLETE_ACTION.VIEW_DRAFT) {
          navigate(routes.LEADLIST);
        }
        setLoading(false);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType(ALERT_TYPE.ERROR);
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  return (
    <div className="dashboard-chart-section-cards notes-widget">
      <div className="left-section">
        <div className="step-parent">
          <div className="step-heading">
            <StepIcon className="step-icon" />
            <div className="step-scan">Steps to Scan</div>
          </div>
          <div className="steps-count-parent">
            <div className="left-arrow">
              <ArrowIcon className="arrow-img" />
              <div className="last-dot"></div>
            </div>
            <div className="steps-right">
              <div>Step 1 - Tap the scan icon to start </div>
              <div>Step 2 - Choose layout (portrait or landscape)</div>
              <div>Step 3 - Scan using your device camera.</div>
              <div>Step 4 - Verify details to publish the lead.</div>
            </div>
          </div>
        </div>
      </div>
      <div className="right-section">
        <div
          className={`scanner-icon-parent ${editMode ? "disabled" : ""}`}
        >
          <img
            className={`scanner-icon ${editMode ? "disabled" : ""}`}
            src={ScannerIcon}
            alt="ScannerIcon"
            onClick={(e: any) => {
              setShowScannerDialog(true);
            }}
          />
        </div>
        <div className="tap-scan">Tap to scan</div>
        <div className="tap-scan-detail">
          Automatically extract and save lead details from cards.
        </div>
      </div>

      <BusinessCardScannerDialog
        open={showScannerDialog}
        setAlertOpen={setAlertOpen}
        setAlertMessage={setAlertMessage}
        setAlertType={setAlertType}
        onClose={() => setShowScannerDialog(false)}
        onScanComplete={(value: BusinessCardType) => {
          setShowScannerDialog(false);
          setCardScanValue(value);
          setShowScanCompleteDialog(true);
        }}
      />
      <ScanCompleteDialog
        open={showScanCompleteDialog}
        onClose={() => setShowScanCompleteDialog(false)}
        onViewDraft={() => {
          createDraftLead(SCAN_COMPLETE_ACTION.VIEW_DRAFT);
        }}
        onVerifyLater={() => {
          createDraftLead(SCAN_COMPLETE_ACTION.VERIFY_LATER);
        }}
      />
    </div>
  );
};

export default BusinessCardScanner;
